import { Beer, UserInteractionsState } from './types';

/**
 * Helper functions for beer status checks
 */

/**
 * Helper function to check if a beer has been tried by the user
 * A beer is considered tried if it has a rating, notes, or is explicitly marked as tried
 * @param {Beer | string} beerOrId - The beer object or beer ID to check
 * @param {UserInteractionsState} userInteractions - The user interactions state object
 * @returns {boolean} True if the beer has been tried
 */
export const isBeerTried = (beerOrId: Beer | string, userInteractions: UserInteractionsState): boolean => {
  const { ratings, notes } = userInteractions;
  const beerId = typeof beerOrId === 'string' ? beerOrId : beerOrId.id;
  const hasRating = (ratings[beerId] ?? 0) > 0;
  const hasNotes = Boolean(notes[beerId] && notes[beerId].trim() !== '');
  return Boolean(hasRating || hasNotes);
};

/**
 * Helper function to check if a beer is favorited by the user
 * @param {Beer | string} beerOrId - The beer object or beer ID to check
 * @param {UserInteractionsState | string[]} userInteractionsOrFavorites - Either the full user interactions state or just the favorites array
 * @returns {boolean} True if the beer is favorited
 */
export const isBeerFavorite = (beerOrId: Beer | string, userInteractionsOrFavorites: UserInteractionsState | string[]): boolean => {
  const beerId = typeof beerOrId === 'string' ? beerOrId : beerOrId.id;
  // Check if we got the full userInteractions object or just the favorites array
  const favorites = Array.isArray(userInteractionsOrFavorites) 
    ? userInteractionsOrFavorites 
    : userInteractionsOrFavorites.favorites;
  return favorites.includes(beerId);
};
